import { z } from 'zod';
import { ErrorResponseSchema } from '@/schemas/common/base';
import { idValidator } from '@/schemas/common/validators';

/**
 * GET /api/chat/[id] - Get chat messages
 * DELETE /api/chat/[id] - Delete chat
 */

// Request param schema
export const ChatIdParamSchema = z.object({
  id: idValidator,
});

export type ChatIdParam = z.infer<typeof ChatIdParamSchema>;

// User schema for response
export const ChatUserSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  name: z.string(),
  email: z.string(),
  trustLevel: z.number(),
  photos: z.object({
    url: z.string(),
    thumbUrl: z.string(),
  }),
});

export type ChatUser = z.infer<typeof ChatUserSchema>;

// Message schema for response
export const ChatMessageSchema = z.object({
  id: z.string(),
  chatId: z.string(),
  senderId: z.string(),
  userId: z.string(),
  text: z.string().nullable(),
  message: z.string().nullable(),
  createdDate: z.string(),
  timestamp: z.string(),
  read: z.boolean(),
});

export type ChatMessage = z.infer<typeof ChatMessageSchema>;

// Item schema for response
export const ChatItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  price: z.number(),
  status: z.number(),
  hasPhoto: z.number(),
  photos: z.array(z.object({
    url: z.string(),
    thumbUrl: z.string(),
  })),
  user: ChatUserSchema.nullable(),
});

export type ChatItem = z.infer<typeof ChatItemSchema>;

// GET response schema - using discriminatedUnion
export const GetChatResponseSchema = z.discriminatedUnion('success', [
  z.object({
    success: z.literal(true),
    chat: z.object({
      id: z.string(),
      listingId: z.string(),
    }),
    messages: z.array(ChatMessageSchema),
    buyer: ChatUserSchema.nullable(),
    seller: ChatUserSchema.nullable(),
    /**
     * The other person, relative to the signed-in user. The server knows who
     * is asking; the client, on a deep link, may not have the profile yet.
     */
    companion: ChatUserSchema.nullable().optional(),
    item: ChatItemSchema.nullable(),
  }),
  ErrorResponseSchema,
]);

export type GetChatResponse = z.infer<typeof GetChatResponseSchema>;

// DELETE response schema - using discriminatedUnion
export const DeleteChatResponseSchema = z.discriminatedUnion('success', [
  z.object({
    success: z.literal(true),
    message: z.string(),
  }),
  ErrorResponseSchema,
]);

export type DeleteChatResponse = z.infer<typeof DeleteChatResponseSchema>;
